// LD-2448
// Enforce one action per physical key press on activatable controls, and block
// keyboard activation entirely while a mouse button is held.
//
// A focused button/link/checkbox activates on Enter/Space via a synthesized
// click. Holding the key (OS repeat), or holding the mouse and tapping Enter,
// fires that click dozens of times a second — faster than any handler can
// debounce (e.g. confirm-alarm spam). We cancel the extra activations in the
// capture phase, before component handlers or the default click run.
//
// Scope is narrow so normal input is untouched: only Enter/Space, and only on
// activatable controls (never text fields).

const ACTIVATABLE = 'button, [role="button"], a[href], ' +
  'input[type="submit"], input[type="button"], input[type="radio"], input[type="checkbox"]'

const ACTIVATION_CODES = new Set(['Enter', 'NumpadEnter', 'Space'])

export function initInputGuard() {
  // Activation keys currently held. Present means a later keydown is an OS repeat.
  const heldKeys = new Set()
  let pointerDown = false

  // Any mouse button engages the gate. `buttons` is the live bitmask of pressed
  // buttons, so the gate clears only once every button is released.
  document.addEventListener('pointerdown', () => { pointerDown = true }, { capture: true })
  window.addEventListener('pointerup', (event) => { pointerDown = event.buttons !== 0 }, { capture: true })
  window.addEventListener('pointercancel', () => { pointerDown = false }, { capture: true })

  document.addEventListener('keydown', (event) => {
    if (!ACTIVATION_CODES.has(event.code)) return

    const target = event.target
    if (!(target instanceof Element) || !target.closest(ACTIVATABLE)) return

    // Mouse held — drop keyboard activation.
    if (pointerDown) {
      event.preventDefault()
      return
    }

    // Not released since its first keydown — this is a repeat.
    if (heldKeys.has(event.code)) {
      event.preventDefault()
      return
    }
    heldKeys.add(event.code)
  }, { capture: true })

  document.addEventListener('keyup', (event) => {
    heldKeys.delete(event.code)

    // Space activates on keyup (Enter on keydown), so the keydown gate can miss
    // it — cancel here too while the mouse is held.
    if (!pointerDown || !ACTIVATION_CODES.has(event.code)) return
    const target = event.target
    if (target instanceof Element && target.closest(ACTIVATABLE)) {
      event.preventDefault()
    }
  }, { capture: true })

  // An up event can be missed if focus leaves mid-press; reset on blur so a held
  // key or button can't wedge activation off.
  window.addEventListener('blur', () => {
    heldKeys.clear()
    pointerDown = false
  })
}
